import { Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { ModalController } from '@ionic/angular';
import { BottomSheetOptions, BottomSheetType, SectionData, SelectedValues, SelectorValue } from './bottom-sheet.model';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { EventModel } from '@boxx/events-core/lib/models/events.model';
import { format, parseISO } from 'date-fns';
import * as moment from 'moment';
import { STATES_DATA } from 'src/assets/statesData';
import Swiper from 'swiper';
import { SocialSharing } from '@ionic-native/social-sharing/ngx';
import { NFCFacade } from '@app/pages/nfc/state/facades/nfc.facade';
import { NFCService } from '@app/pages/nfc/services/nfc.service';
import { BoxxUtilsService } from 'src/services/BoxxUtils.service';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';

@Component({
  selector: 'app-bottom-sheet',
  templateUrl: './bottom-sheet.component.html',
  styleUrls: ['./bottom-sheet.component.scss']
})
export class BottomSheetComponent implements OnInit {

  swiperConfig = {
    pagination: {
      el: '.swiper-pagination',
      clickable: true
    }
  };
  
  @Input() config: BottomSheetOptions;
  selectedValue: string | SelectedValues;
  filteredData: any = [];
  QRMessage: string;

  form: FormGroup = new FormGroup({
    quantity: new FormControl(6, [Validators.required, Validators.min(0)])
  });
  labelMinMax: string;
  websiteUrlString: string;
  translations: any;
  destroyed$ = new Subject<boolean>();
  isCordovaOrCapacitor = false;
  event: EventModel;
  indexSelected: any = null;

  customMinutes = [10, 15, 20, 30, 45, 60, 90, 120];

  types = STATES_DATA;

  langList = [
    {
      key: 'es',
      name: 'STORE_MODULE.profile_VIEW.company_language_spanish',
      icon: './assets/country-flags/svg/mx.svg'
    },
    {
      key: 'en',
      name: 'STORE_MODULE.profile_VIEW.company_language_english',
      icon: './assets/country-flags/svg/us.svg'
    }
  ];

  @ViewChild('swipperSlider')
  swiperRef: ElementRef | undefined;
  swiper?: Swiper;

  currentIndex = 0;
  isNFCFinish = false;

  public isSupported = false;
  public isPermissionGranted = false;

  @Input() prefix: string;
  defaultValue: string;

  // theme: ThemeEnum = ThemeEnum.omni;
  constructor(
    private modalCtrl: ModalController,
    private translate: TranslateService,
    private socialSharing: SocialSharing,
    private ngZone: NgZone,
    private nfcService: NFCService,
    private utils: BoxxUtilsService,
    private nfcFacade: NFCFacade
  ) {
    this.translate
      .get(['QR', 'CLIPBOARD', 'PANEL_VIEW', 'EVENTS_MODULE', 'ACTIONS'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((t: any) => (this.translations = t));
  }

  async ngOnInit() {
    //this.theme = await this.themeService.getTheme();
    this.filteredData = [...this.config.data];
    if (this.config.type === 'numericInput') {
      this.createForm();
    }

    if (this.config.tagCode) {
      this.prefix = this.config.tagCode + ' - ';
      this.defaultValue = this.prefix + ((this.config.previousName) ? this.config.previousName : '');
    }

    const platform = Capacitor.getPlatform();
    if (platform === 'android' || platform === 'ios') {
      BarcodeScanner.isSupported().then((result) => {
        this.isSupported = result.supported;
      });
      BarcodeScanner.checkPermissions().then((result) => {
        this.isPermissionGranted = result.camera === 'granted';
      });
      BarcodeScanner.removeAllListeners().then(() => {
        BarcodeScanner.addListener(
          'googleBarcodeScannerModuleInstallProgress',
          (event) => {
            this.ngZone.run(() => {
              console.log('googleBarcodeScannerModuleInstallProgress', event);
              const { state, progress } = event;
            });
          },
        );
      });
    }

    if (this.config?.type === 'nfc') {
      if (this.config.statusAllowNFC === 1) {
        this.QRMessage = this.translations.PANEL_VIEW.NFC.nfcOption;
      }
      else if (this.config.statusAllowNFC === 2) {
        this.QRMessage = this.translations.PANEL_VIEW.NFC.qrOption;
      }
      else if (this.config.statusAllowNFC === 3) {
        this.QRMessage = this.translations.PANEL_VIEW.NFC.nfcSuccess;
      }
    }
  }
  
  ionViewDidEnter() {
    if (this.config?.currentValue) {
      if (this.config.type === BottomSheetType.multiAccordion) {
        this.selectedValue = [...this.config.currentValue];
        this.onValueChange();
      } else {
        this.selectedValue = this.config.currentValue;
      }
    }

    if (this.config?.type === 'qr') {
      this.validateSiteQR();
    }
    if (this.config?.type === 'event') {
      this.event = this.config.extraData;
    }
  }

  validateSiteQR() {
    if (!this.config?.firstTimeOpen) {
      if (this.config?.site.type === 'agency_website' || this.config?.site.type === 'website') {
        this.QRMessage = this.translations.PANEL_VIEW.WIDGET.QR.alternativeTitle;
      }
      else if (this.config?.site.type === 'shop') {
        this.QRMessage = this.translations.PANEL_VIEW.WIDGET.QR.alternativeTitleStore;
      }
      else if (this.config?.site.type === 'directory') {
        this.QRMessage = this.translations.PANEL_VIEW.WIDGET.QR.alternativeTitleDirectory;
      }
    }
    else {
      setTimeout(async () => {
        if (this.config?.site.type === 'agency_website' || this.config?.site.type === 'website') {
          this.QRMessage = this.translations.PANEL_VIEW.WIDGET.QR.alternativeTitle;
        }
        else if (this.config?.site.type === 'shop') {
          this.QRMessage = this.translations.PANEL_VIEW.WIDGET.QR.alternativeTitleStore;
        }
        else if (this.config?.site.type === 'directory') {
          this.QRMessage = this.translations.PANEL_VIEW.WIDGET.QR.alternativeTitleDirectory;
        }
      }, 100);
    }
    this.websiteUrlString = this.config?.site.url;
    console.log('this.websiteUrlString: ', this.websiteUrlString);
  }

  onSelect() {
    if (this.config.type === 'numericInput') {
      const val = this.form?.controls.quantity.value;
      this.modalCtrl.dismiss({ value: val });
      return;
    }
    else if (this.config.type === 'gbp_options') {
        this.modalCtrl.dismiss({ value: this.indexSelected });
        return;
    }
    else{
      this.modalCtrl.dismiss({ value: this.selectedValue });
    }
  }

  onItemSelect(itemValue) {
    this.modalCtrl.dismiss({ value: itemValue });
  }

  onSiteSelect(itemValue, url) {
    this.modalCtrl.dismiss({ value: itemValue, url });
  }

  createForm() {
    //add value
    this.form.controls.quantity?.setValue(this.config.currentValue);
    // add validators
    //this.form.controls.quantity?.addValidators([Validators.max(this.config.maxValue || 99)]);
  }
  onValueChange() {
    this.modalCtrl.getTop().then(modal => {
      if (this.selectedValue || this.selectedValue?.length > 0) {
        modal.setCurrentBreakpoint(1);
      } else {
        modal.setCurrentBreakpoint(this.config.initialBreakpoint);
      }
    })
  }
  fullOpen() {
    this.modalCtrl.getTop().then(modal => {
      modal.setCurrentBreakpoint(1);
    });
  }

  onCancelAction() {
    this.selectedValue = null;
    this.modalCtrl.dismiss({ value: { isCancel: true } });
  }

  itemSelected(item: SelectorValue, sectionKey: string) {
    const existingValue = (this.selectedValue as SelectedValues).find(value => value.sectionKey === sectionKey);
    if (existingValue) {
      const existingIndex = (this.selectedValue as SelectedValues).findIndex(value => value.sectionKey === sectionKey);
      (this.selectedValue as SelectedValues)[existingIndex] = { sectionKey, item };
    } else {
      (this.selectedValue as SelectedValues).push({ sectionKey, item });
    }
    this.onValueChange();
  }

  getAccordionGroupValues() {
    let listSectionKeys: string[] = [];
    if (this.selectedValue?.length > 0) {
      (this.selectedValue as SelectedValues).forEach(currentValue => {
        const data = this.config.data as SectionData[];
        const fetchData = data.find(value => value.section.key === currentValue?.sectionKey);
        listSectionKeys.push(`${fetchData.section.key}`);
      });
    }
    return listSectionKeys;
  }

  getRadioValue(sectionData: SectionData) {
    if (this.selectedValue?.length > 0) {
      const sectionExist = (this.selectedValue as SelectedValues).find(value => value.sectionKey === sectionData.section.key);
      if (sectionExist) {
        return sectionData.items.find(item => item.key === sectionExist.item.key);
      }
    }
  }

  onSearchChange(e) {

    let value: string = e.detail.value;
    if (!value?.length) {
      this.filteredData = [...this.config.data];
      return;
    }
    value = value.toLocaleLowerCase();
    this.filteredData = [...this.config.data].filter((x: any) => {
      if (typeof x === 'string') {
        return x.toLocaleLowerCase().includes(value);
      }
      // !Caution, if translation is passed as value only search by key will work
      if (
        x.key.toLocaleLowerCase().includes(value) ||
        x.value.toLocaleLowerCase().includes(value)
      ) {
        return true;
      }
      return false;
    })
  }

  closeKeyboard() {
    if (Capacitor.isNativePlatform()) {
      Keyboard.hide().catch(console.warn);
    }
  }

  getIconOpt(iconName: string) {
    return `assets/icon/${iconName}.svg`;
  }

  closeActionSheet() {
    this.modalCtrl.dismiss();
  }

  dateFormat(date: string) {
    if (date && date !== null && date !== undefined) {
      return format(parseISO(moment(date).format()), 'dd MMM yyyy - HH:mm');
    }
  }

  selectState(state: any) {
    this.modalCtrl.dismiss({ value: state });
  }

  selectLanguage(lng: any) {
    this.modalCtrl.dismiss({ value: lng });
  }

  swiperReady() {
    this.swiper = this.swiperRef?.nativeElement.swiper;
  }

  swiperSlideChanged(e: any) { }

  async shareUrl(url: string, url_type: string) {
    const platform = Capacitor.getPlatform();
    let _title = '';
    switch (url_type) {
      case 'agency_website':
      case 'website':
      case 'directory':
        _title = this.translations.ACTIONS.shareSite;
        break;
      case 'shop':
        _title = this.translations.ACTIONS.shareStore;
        break;

      default:
        break;
    }
    console.log('_title: ', _title)
    if (platform === 'android' || platform === 'ios') {
      try {
        this.socialSharing.share(_title, _title, null, url)
          .then(() => { }).catch((error) => { });

        // await Share.share({
        //   title: _title,
        //   text: url,
        //   dialogTitle: _title,
        // });
      } catch (error) { }
      return;
    }
  }


  setNameCard(){
    this.config.statusAllowNFC = 4;
    const modalElement = document.querySelector('.bottom-sheet-modal');
    if (modalElement) {
      modalElement.classList.remove('bottom-sheet-modal-70'); // Remueve la clase actual
      modalElement.classList.add('bottom-sheet-modal-40'); // Agrega la nueva clase
    }
  }

  closeNFC(){
    this.modalCtrl.dismiss();
  }

  preventPrefixEdit(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;

    if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      inputElement.selectionStart !== null &&
      inputElement.selectionStart <= this.prefix.length
    ) {
      event.preventDefault();
    }

    if (
      inputElement.selectionStart !== null &&
      inputElement.selectionStart < this.prefix.length
    ) {
      inputElement.setSelectionRange(this.prefix.length, this.prefix.length);
    }
  }

  onInputChange(event: any): void {
    const inputValue: string = event.target.value;

    if (!inputValue.startsWith(this.prefix)) {
      event.target.value = this.defaultValue;
    } else {
      this.defaultValue = inputValue; 
    }
  }

  acceptNFC(){
    const result = this.defaultValue.split('-')[1]?.trim(); // Toma el texto después del "-"
    this.utils.handleLoading(true);
    this.nfcService.updateNFC({code: this.config.tagCode, name: result})
    .subscribe({
      next: (response: any) => {
        this.utils.handleLoading(false);
        this.nfcFacade.loadNFCDevices();
        this.modalCtrl.dismiss({ value: 'update-nfc', data: result });
      },
      error: (error: any) => {
        console.log('error: ', error);
        this.utils.handleLoading(false);
      }
    });

  }

  setIndexSelected(idx: any){
    this.indexSelected = idx;
  }
}
